/**
 * Infers the target role for a notification based on type and context.
 * Used to auto-switch accounts for dual-role users.
 */
export const inferTargetRole = (
  notification: { type: string; data?: Record<string, any> }
): 'filmmaker' | 'client' | null => {
  const { type, data } = notification;

  // If explicitly set in notification data, use that
  if (data?.target_role) {
    return data.target_role as 'filmmaker' | 'client';
  }

  // For messages, recipient is opposite of sender
  if (type === 'message' || type === 'message_notification' || type === 'new_message') {
    const senderRole = data?.sender_role || data?.senderRole;
    if (senderRole === 'filmmaker') return 'client';
    if (senderRole === 'client') return 'filmmaker';
    // If no sender_role, can't determine
    return null;
  }

  // For document uploads, recipient is opposite of uploader
  if (type === 'document_uploaded') {
    const uploaderType = data?.uploader_type;
    if (uploaderType === 'filmmaker') return 'client';
    if (uploaderType === 'client') return 'filmmaker';
    return null;
  }

  // Notifications that are ALWAYS for filmmakers
  const filmmakerNotifications = [
    'job_match',                    // Filmmaker gets matched to jobs
    'booking_request_received',     // Filmmaker receives booking request
    'booking_link_confirmation_filmmaker', // Filmmaker booking link confirmation
    'payment_received',             // Filmmaker receives payment
    'payout_notification',          // Filmmaker payout
    'deck_invitation',              // Filmmaker invited to pitch
    'nda_reminder',                 // Filmmaker NDA reminder
    'pitch_selected',               // Filmmaker's pitch was selected
    'pitch_status_update',          // Filmmaker's pitch status changed
    'application_rejected',         // Filmmaker's application was rejected
    'contract_offered',             // Filmmaker offered a contract
    'review_reminder',              // Filmmaker review reminder
    'welcome',                      // System notifications for filmmaker
    'profile_reminder',
    'verification_reminder',
    'account_approved',
    'direct_hire',                  // Filmmaker receives direct hire offer
  ];

  // Notifications that are ALWAYS for clients
  const clientNotifications = [
    'application_received',         // Client receives applications
    'new_applicant',                // Client has new applicant
    'booking_confirmation',         // Client's booking confirmed
    'booking_link_confirmation_client', // Client booking link confirmation
    'booking_request_accepted',     // Client's request accepted
    'booking_request_rejected',     // Client's request rejected
    'payment_reminder',             // Client needs to pay
    'deck_new_submission',          // Client receives pitch submission
    'deliverable_submitted',        // Client receives deliverable
    'direct_hire_accepted',         // Filmmaker accepted direct hire
    'direct_hire_declined',         // Filmmaker declined direct hire
  ];

  // Notifications that depend on context but have sensible defaults
  const filmmakerDefaultNotifications = [
    'job_application',              // Usually filmmaker checking their application
    'application_submitted',        // Filmmaker submitted application
    'job_status_update',            // Filmmaker checking job status
    'contract_signed',              // Filmmaker's contract signed
    'booking_request',              // Could be either, default filmmaker
    'booking_reminder',             // Could be either, default filmmaker
    'calendar_reminder',            // Calendar reminders - default to filmmaker
    'calendar_invite',              // Calendar invite - could be either
    'calendar_group_invite',        // Group calendar invite
    'calendar_update',              // Calendar event update
    'calendar_response',            // Response to calendar invite
    'calendar_attendee_response',   // Attendee responded to event
    'message_reaction',             // Reaction to message
    'group_invite',                 // Invited to message group
    'broadcast',                    // System broadcast
  ];

  const clientDefaultNotifications = [
    'deliverable_approved',         // Client approved deliverable
    'deliverable_revision_requested', // Client requested revision
  ];

  if (filmmakerNotifications.includes(type)) {
    return 'filmmaker';
  }

  if (clientNotifications.includes(type)) {
    return 'client';
  }

  if (filmmakerDefaultNotifications.includes(type)) {
    return 'filmmaker';
  }

  if (clientDefaultNotifications.includes(type)) {
    return 'client';
  }

  // For ambiguous notifications, try to infer from data
  // If notification has filmmaker_id but no client_id context, it's likely for filmmaker
  if (data?.filmmaker_id && !data?.client_id) {
    return 'filmmaker';
  }
  if (data?.client_id && !data?.filmmaker_id) {
    return 'client';
  }

  // Can't determine - return null to use current role
  return null;
};

/**
 * Gets the navigation URL for a notification based on its type and the target role.
 * Returns the appropriate dashboard path for dual-account users.
 */
export const getNotificationNavigationUrl = (
  notification: { type: string; data?: Record<string, any> },
  targetRole: 'filmmaker' | 'client'
): string => {
  const { type, data } = notification;

  switch (type) {
    // Job notifications
    case 'job_match':
      return data?.job_id
        ? `/filmmaker/dashboard/jobs/${data.job_id}`
        : '/filmmaker/dashboard/jobs';
    case 'application_submitted':
      return data?.job_id
        ? `/filmmaker/dashboard/jobs/${data.job_id}`
        : '/filmmaker/dashboard/applications';
    case 'application_received':
      return data?.job_id
        ? `/client/dashboard/hiring?job=${data.job_id}`
        : '/client/dashboard/hiring';
    case 'application_rejected':
      return '/filmmaker/dashboard/applications';
    case 'contract_offered':
      return data?.bid_id
        ? `/filmmaker/dashboard/contract/${data.bid_id}`
        : '/filmmaker/dashboard/applications';
    case 'contract_signed':
      return '/filmmaker/dashboard/applications';

    // Direct hire notifications
    case 'direct_hire':
      // Filmmaker receives direct hire - navigate to job details where they can accept/decline
      return data?.job_id
        ? `/filmmaker/dashboard/jobs/${data.job_id}?direct_hire=${data.bid_id}`
        : '/filmmaker/dashboard/applications';
    case 'direct_hire_accepted':
    case 'direct_hire_declined':
      // Client sees response - navigate to hiring page with job pre-selected
      return data?.job_id
        ? `/client/dashboard/hiring?job=${data.job_id}`
        : '/client/dashboard/hiring';

    // Booking notifications
    case 'booking_request_received':
    case 'booking_reminder':
    case 'booking_link_confirmation_filmmaker':
      return '/filmmaker/dashboard/bookings';
    case 'booking_confirmation':
    case 'booking_link_confirmation_client':
      return '/client/dashboard/bookings';
    case 'booking_request_accepted':
    case 'booking_request_rejected':
      return '/client/dashboard';

    // Pitching Room notifications
    case 'deck_invitation':
    case 'pitch_status_update':
    case 'pitch_selected':
    case 'nda_reminder':
      return '/filmmaker/dashboard/pitching-room';
    case 'deck_new_submission':
      return '/client/dashboard/pitching-room';

    // Payment notifications
    case 'payment_reminder':
      return '/client/dashboard';
    case 'payment_received':
    case 'payout_notification':
      return '/filmmaker/dashboard/earnings';

    // Deliverables
    case 'deliverable_submitted':
      return '/client/dashboard';
    case 'deliverable_approved':
    case 'deliverable_revision_requested':
      return '/filmmaker/dashboard';

    // System notifications
    case 'welcome':
    case 'profile_reminder':
    case 'verification_reminder':
      return `/${targetRole}/dashboard/account`;
    case 'account_approved':
      return `/${targetRole}/dashboard`;

    // Review notifications
    case 'review_reminder':
      return '/filmmaker/dashboard/reviews';

    // Message notifications - use the target role determined by inferTargetRole
    case 'message_notification':
    case 'new_message':
      if (data?.room_name) {
        return `/${targetRole}/dashboard/messages?room=${data.room_name}`;
      }
      return `/${targetRole}/dashboard/messages`;

    // Calendar notifications - navigate to calendar with event if available
    case 'calendar_reminder':
    case 'calendar_invite':
    case 'calendar_group_invite':
    case 'calendar_update':
    case 'calendar_response':
    case 'calendar_attendee_response':
      if (data?.event_id) {
        return `/${targetRole}/dashboard/calendar?event=${data.event_id}`;
      }
      return `/${targetRole}/dashboard/calendar`;

    // Message reaction and group invite
    case 'message':
    case 'message_reaction':
      if (data?.room_name) {
        return `/${targetRole}/dashboard/messages?room=${data.room_name}`;
      }
      return `/${targetRole}/dashboard/messages`;
    case 'group_invite':
      return `/${targetRole}/dashboard/messages`;

    // System broadcast
    case 'broadcast':
      return `/${targetRole}/dashboard`;

    // Document uploaded - navigate to project documents page
    case 'document_uploaded':
      if (data?.link) {
        return data.link;
      }
      if (data?.project_id) {
        return `/${targetRole}/dashboard/projects/${data.project_id}/documents`;
      }
      return `/${targetRole}/dashboard`;

    default:
      return `/${targetRole}/dashboard`;
  }
};
